import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { 
  Plus, Package, CheckCircle, XCircle, Clock, Search, Filter,
  DollarSign, Tag, Building2, ChevronRight, History, Edit, Trash2,
  Wine, UtensilsCrossed, Coffee, RefreshCw
} from 'lucide-react';
import { getErrorMessage } from '../lib/utils';

import { API } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

const SUPER_CATEGORIES = ['Food', 'Liquor', 'Normal-Drinks'];

const getSuperCategoryIcon = (superCat) => {
  switch(superCat) {
    case 'Food': return <UtensilsCrossed className="w-4 h-4 text-orange-600" />;
    case 'Liquor': return <Wine className="w-4 h-4 text-purple-600" />;
    case 'Normal-Drinks': return <Coffee className="w-4 h-4 text-blue-600" />;
    default: return <Package className="w-4 h-4" />;
  }
};

const getStatusBadge = (status) => {
  switch(status) {
    case 'approved': return <Badge className="bg-green-100 text-green-800">Approved</Badge>;
    case 'pending': return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
    case 'rejected': return <Badge className="bg-red-100 text-red-800">Rejected</Badge>;
    default: return <Badge>{status}</Badge>;
  }
};


export default function PriceMaster({ user }) {
  const [activeTab, setActiveTab] = useState('items');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Data
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [prices, setPrices] = useState([]);
  const [summary, setSummary] = useState({});
  const [pendingItems, setPendingItems] = useState([]);
  
  // Filters
  const [filterSuperCategory, setFilterSuperCategory] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // list, category, vendor
  
  // Dialogs
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddPrice, setShowAddPrice] = useState(false);
  const [showItemHistory, setShowItemHistory] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectItemId, setRejectItemId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Forms
  const [newItem, setNewItem] = useState({
    item_name: '',
    super_category: '',
    category_id: '',
    company: '',
    unit: '',
    piece: '',
    vendor_id: ''
  });
  
  const [newCategory, setNewCategory] = useState({
    name: '',
    super_category: ''
  });
  
  const [newPrice, setNewPrice] = useState({
    item_id: '',
    month: new Date().toISOString().slice(0, 7),
    price: '',
    remarks: ''
  });

  const canManageItems = user?.role === 'storekeeper' || user?.role === 'admin';
  const canApprove = user?.role === 'admin';
  const canManageCategories = user?.role === 'admin';

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      
      const [categoriesRes, vendorsRes, itemsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/price-master/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/price-master/items`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/price-master/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setCategories(categoriesRes.data);
      setVendors(vendorsRes.data);
      setItems(itemsRes.data);
      setSummary(summaryRes.data);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Price Master fetch error:', error);
      toast.error(error.message || 'Failed to fetch data', {
        action: {
          label: 'Retry',
          onClick: () => fetchData()
        }
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'prices') {
      fetchPrices();
    } else if (activeTab === 'pending' && canApprove) {
      fetchPendingItems();
    }
  }, [activeTab, filterMonth, filterSuperCategory, filterCategory, filterVendor, viewMode, canApprove]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchData(true), 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleManualRefresh = () => {
    fetchData(true);
    toast.success('Data refreshed!');
  };

  const fetchPrices = async () => {
    try {
      const token = localStorage.getItem('token');
      let endpoint = `${API}/price-master/prices`;
      
      if (viewMode === 'category') {
        endpoint = `${API}/price-master/prices/by-category`;
      } else if (viewMode === 'vendor') {
        endpoint = `${API}/price-master/prices/by-vendor`;
      }
      
      const params = { month: filterMonth };
      if (filterSuperCategory !== 'all') params.super_category = filterSuperCategory;
      if (filterCategory !== 'all') params.category_id = filterCategory;
      if (filterVendor !== 'all') params.vendor_id = filterVendor;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setPrices(response.data);
    } catch (error) {
      toast.error('Failed to fetch prices');
    }
  };

  const fetchPendingItems = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/price-master/items/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch pending items');
    }
  };

  const fetchItemHistory = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/price-master/prices/item-history/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedItem(response.data);
      setShowItemHistory(true);
    } catch (error) {
      toast.error('Failed to fetch item history');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/price-master/items`, newItem, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item submitted for approval');
      setShowAddItem(false);
      setNewItem({ item_name: '', super_category: '', category_id: '', company: '', unit: '', piece: '', vendor_id: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add item'));
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/price-master/categories`, newCategory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category created');
      setShowAddCategory(false);
      setNewCategory({ name: '', super_category: '' });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add category'));
    }
  };

  const handleAddPrice = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/price-master/prices`, {
        ...newPrice,
        price: parseFloat(newPrice.price)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Price added');
      setShowAddPrice(false);
      setNewPrice({ item_id: '', month: new Date().toISOString().slice(0, 7), price: '', remarks: '' });
      fetchPrices();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add price'));
    }
  };

  const handleApproveItem = async (itemId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/price-master/items/${itemId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item approved');
      fetchPendingItems();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to approve item'));
    }
  };

  const handleRejectItem = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/price-master/items/${rejectItemId}/reject`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        params: { reason: rejectReason }
      });
      toast.success('Item rejected');
      setShowRejectDialog(false);
      setRejectItemId(null);
      setRejectReason('');
      fetchPendingItems();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reject item'));
    }
  };

  const filteredItems = items.filter(item => {
    if (filterSuperCategory !== 'all' && item.super_category !== filterSuperCategory) return false;
    if (filterCategory !== 'all' && item.category_id !== filterCategory) return false;
    if (filterVendor !== 'all' && item.vendor_id !== filterVendor) return false;
    if (searchTerm && !item.item_name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const approvedItems = items.filter(item => item.status === 'approved');

  const filteredCategories = filterSuperCategory !== 'all' 
    ? categories.filter(c => c.super_category === filterSuperCategory)
    : categories;

  return (
    <div className="space-y-6">
      {/* Refresh Bar */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Last updated: {lastRefresh.toLocaleTimeString('en-IN')}
          {refreshing && <span className="ml-2 text-blue-500">• Refreshing...</span>}
        </p>
        <Button variant="outline" size="sm" onClick={handleManualRefresh} disabled={refreshing} className="gap-2">
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Store Purchase Price Master</h1>
          <p className="text-gray-600">Manage items, categories, and monthly prices</p>
        </div>
        <div className="flex gap-2">
          {canManageCategories && (
            <Dialog open={showAddCategory} onOpenChange={setShowAddCategory}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Tag className="w-4 h-4 mr-2" />
                  Add Category
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add New Category</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddCategory} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Super Category</Label>
                    <Select
                      value={newCategory.super_category}
                      onValueChange={(v) => setNewCategory({ ...newCategory, super_category: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select super category" />
                      </SelectTrigger>
                      <SelectContent>
                        {SUPER_CATEGORIES.map(sc => (
                          <SelectItem key={sc} value={sc}>
                            <span className="flex items-center gap-2">
                              {getSuperCategoryIcon(sc)} {sc}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Category Name</Label>
                    <Input
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      placeholder="e.g., Vegetables, Whiskey, Soft Drinks"
                      required
                    />
                  </div>
                  <DialogFooter>
                    <Button type="submit">Create Category</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
          
          {canManageItems && (
            <Dialog open={showAddItem} onOpenChange={setShowAddItem}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Add New Item</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddItem} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Item Name *</Label>
                    <Input
                      value={newItem.item_name}
                      onChange={(e) => setNewItem({ ...newItem, item_name: e.target.value })}
                      placeholder="Enter item name"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Super Category *</Label>
                      <Select
                        value={newItem.super_category}
                        onValueChange={(v) => setNewItem({ ...newItem, super_category: v, category_id: '' })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {SUPER_CATEGORIES.map(sc => (
                            <SelectItem key={sc} value={sc}>
                              <span className="flex items-center gap-2">
                                {getSuperCategoryIcon(sc)} {sc}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select
                        value={newItem.category_id}
                        onValueChange={(v) => setNewItem({ ...newItem, category_id: v })}
                        disabled={!newItem.super_category}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories
                            .filter(c => c.super_category === newItem.super_category)
                            .map(cat => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Company *</Label>
                    <Input
                      value={newItem.company}
                      onChange={(e) => setNewItem({ ...newItem, company: e.target.value })}
                      placeholder="Brand/Company name"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Unit *</Label>
                      <Input
                        value={newItem.unit}
                        onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                        placeholder="e.g., kg, ltr, box"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Piece</Label>
                      <Input
                        value={newItem.piece}
                        onChange={(e) => setNewItem({ ...newItem, piece: e.target.value })}
                        placeholder="e.g., 12 per box"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Vendor *</Label>
                    <Select
                      value={newItem.vendor_id}
                      onValueChange={(v) => setNewItem({ ...newItem, vendor_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.filter(v => v.is_active).map(vendor => (
                          <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <DialogFooter>
                    <Button type="submit">Submit for Approval</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-blue-600 font-medium">Total Items</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{summary.total_items || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-600 font-medium">Approved</span>
            </div>
            <p className="text-2xl font-bold text-green-700">{summary.approved_items || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-yellow-600" />
              <span className="text-sm text-yellow-600 font-medium">Pending Approval</span>
            </div>
            <p className="text-2xl font-bold text-yellow-700">{summary.pending_items || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-purple-600" />
              <span className="text-sm text-purple-600 font-medium">Prices ({summary.current_month})</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">{summary.current_month_prices || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="items">Items Master</TabsTrigger>
          <TabsTrigger value="prices">Price Ledger</TabsTrigger>
          {canApprove && (
            <TabsTrigger value="pending" className="relative">
              Pending Approval
              {summary.pending_items > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs">
                  {summary.pending_items}
                </Badge>
              )}
            </TabsTrigger>
          )}
          <TabsTrigger value="categories">Categories</TabsTrigger>
        </TabsList>

        {/* Items Tab */}
        <TabsContent value="items" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-center gap-4">
                <CardTitle>Items Master</CardTitle>
                <div className="flex flex-wrap gap-2 items-center">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search items..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-48"
                    />
                  </div>
                  <Select value={filterSuperCategory} onValueChange={setFilterSuperCategory}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {SUPER_CATEGORIES.map(sc => (
                        <SelectItem key={sc} value={sc}>{sc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterVendor} onValueChange={setFilterVendor}>
                    <SelectTrigger className="w-40">
                      <SelectValue placeholder="All Vendors" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Vendors</SelectItem>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">Loading...</div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No items found</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1">
                            {getSuperCategoryIcon(item.super_category)}
                            {item.super_category}
                          </span>
                        </TableCell>
                        <TableCell>{item.category_name}</TableCell>
                        <TableCell>{item.company}</TableCell>
                        <TableCell>{item.unit}{item.piece && ` (${item.piece})`}</TableCell>
                        <TableCell>{item.vendor_name}</TableCell>
                        <TableCell>{getStatusBadge(item.status)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => fetchItemHistory(item.id)}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Prices Tab */}
        <TabsContent value="prices" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap justify-between items-center gap-4">
                <CardTitle>Price Ledger - {filterMonth}</CardTitle>
                <div className="flex flex-wrap gap-2 items-center">
                  <Input
                    type="month"
                    value={filterMonth}
                    onChange={(e) => setFilterMonth(e.target.value)}
                    className="w-40"
                  />
                  <Select value={viewMode} onValueChange={setViewMode}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="list">Price List</SelectItem>
                      <SelectItem value="category">By Category</SelectItem>
                      <SelectItem value="vendor">By Vendor</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterSuperCategory} onValueChange={setFilterSuperCategory}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {SUPER_CATEGORIES.map(sc => (
                        <SelectItem key={sc} value={sc}>{sc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {canManageItems && (
                    <Dialog open={showAddPrice} onOpenChange={setShowAddPrice}>
                      <DialogTrigger asChild>
                        <Button>
                          <DollarSign className="w-4 h-4 mr-2" />
                          Add Price
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Price Entry</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleAddPrice} className="space-y-4">
                          <div className="space-y-2">
                            <Label>Item *</Label>
                            <Select
                              value={newPrice.item_id}
                              onValueChange={(v) => setNewPrice({ ...newPrice, item_id: v })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select approved item" />
                              </SelectTrigger>
                              <SelectContent>
                                {approvedItems.map(item => (
                                  <SelectItem key={item.id} value={item.id}>
                                    {item.item_name} ({item.vendor_name})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Month *</Label>
                              <Input
                                type="month"
                                value={newPrice.month}
                                onChange={(e) => setNewPrice({ ...newPrice, month: e.target.value })}
                                required
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Price (₹) *</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={newPrice.price}
                                onChange={(e) => setNewPrice({ ...newPrice, price: e.target.value })}
                                placeholder="0.00"
                                required
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Remarks</Label>
                            <Input
                              value={newPrice.remarks}
                              onChange={(e) => setNewPrice({ ...newPrice, remarks: e.target.value })}
                              placeholder="Optional notes"
                            />
                          </div>
                          <DialogFooter>
                            <Button type="submit">Add Price</Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {viewMode === 'list' && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prices.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                          No prices found for this month
                        </TableCell>
                      </TableRow>
                    ) : (
                      prices.map((price) => (
                        <TableRow key={price.id}>
                          <TableCell className="font-medium">{price.item_name}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1">
                              {getSuperCategoryIcon(price.super_category)}
                              {price.super_category}
                            </span>
                          </TableCell>
                          <TableCell>{price.category_name}</TableCell>
                          <TableCell>{price.company}</TableCell>
                          <TableCell>{price.unit}</TableCell>
                          <TableCell>{price.vendor_name}</TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(price.price)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
              
              {viewMode === 'category' && (
                <div className="space-y-6">
                  {!prices || prices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No prices found</div>
                  ) : (
                    prices.map((group, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                          {getSuperCategoryIcon(group?.super_category)}
                          {group?.super_category} <ChevronRight className="w-4 h-4" /> {group?.category_name}
                        </h3>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Item</TableHead>
                              <TableHead>Company</TableHead>
                              <TableHead>Vendor</TableHead>
                              <TableHead className="text-right">Price</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(group?.items || []).map((item) => (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">{item.item_name}</TableCell>
                                <TableCell>{item.company}</TableCell>
                                <TableCell>{item.vendor_name}</TableCell>
                                <TableCell className="text-right font-bold text-green-600">
                                  {formatCurrency(item.price)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ))
                  )}
                </div>
              )}
              
              {viewMode === 'vendor' && (
                <div className="space-y-6">
                  {!prices || prices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No prices found</div>
                  ) : (
                    prices.map((group, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          {group?.vendor_name}
                          <Badge variant="outline" className="ml-2">{group?.total_items || 0} items</Badge>
                        </h3>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Item</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Category</TableHead>
                              <TableHead className="text-right">Price</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(group?.items || []).map((item) => (
                              <TableRow key={item.id}>
                                <TableCell className="font-medium">{item.item_name}</TableCell>
                                <TableCell>
                                  <span className="flex items-center gap-1">
                                    {getSuperCategoryIcon(item.super_category)}
                                    {item.super_category}
                                  </span>
                                </TableCell>
                                <TableCell>{item.category_name}</TableCell>
                                <TableCell className="text-right font-bold text-green-600">
                                  {formatCurrency(item.price)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ))
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Approval Tab (Admin only) */}
        {canApprove && (
          <TabsContent value="pending" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Items Pending Approval</CardTitle>
              </CardHeader>
              <CardContent>
                {pendingItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No items pending approval</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Item Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Unit</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Submitted By</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingItems.map((item) => (
                        <TableRow key={item.id} className="bg-yellow-50/50">
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell>
                            <span className="flex items-center gap-1">
                              {getSuperCategoryIcon(item.super_category)}
                              {item.super_category}
                            </span>
                          </TableCell>
                          <TableCell>{item.category_name}</TableCell>
                          <TableCell>{item.company}</TableCell>
                          <TableCell>{item.unit}{item.piece && ` (${item.piece})`}</TableCell>
                          <TableCell>{item.vendor_name}</TableCell>
                          <TableCell>{item.created_by_name}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleApproveItem(item.id)}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => { setRejectItemId(item.id); setShowRejectDialog(true); }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Item Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {SUPER_CATEGORIES.map(superCat => (
                  <div key={superCat} className="border rounded-lg p-4">
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      {getSuperCategoryIcon(superCat)}
                      {superCat}
                    </h3>
                    <div className="space-y-2">
                      {categories
                        .filter(c => c.super_category === superCat)
                        .map(cat => (
                          <div key={cat.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <span>{cat.name}</span>
                          </div>
                        ))}
                      {categories.filter(c => c.super_category === superCat).length === 0 && (
                        <p className="text-gray-500 text-sm">No categories</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a reason for rejection"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleRejectItem}>Reject</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item History Dialog */}
      <Dialog open={showItemHistory} onOpenChange={setShowItemHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Price History - {selectedItem?.item?.item_name}</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Type:</span> {selectedItem.item.super_category}</div>
                <div><span className="text-gray-500">Category:</span> {selectedItem.item.category_name}</div>
                <div><span className="text-gray-500">Company:</span> {selectedItem.item.company}</div>
                <div><span className="text-gray-500">Vendor:</span> {selectedItem.item.vendor_name}</div>
                <div><span className="text-gray-500">Unit:</span> {selectedItem.item.unit}</div>
                <div><span className="text-gray-500">Status:</span> {getStatusBadge(selectedItem.item.status)}</div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Price History</h4>
                {selectedItem.price_history.length === 0 ? (
                  <p className="text-gray-500">No price entries yet</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead>Remarks</TableHead>
                        <TableHead>Added By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedItem.price_history.map((price) => (
                        <TableRow key={price.id}>
                          <TableCell className="font-medium">{price.month}</TableCell>
                          <TableCell className="text-right font-bold text-green-600">
                            {formatCurrency(price.price)}
                          </TableCell>
                          <TableCell>{price.remarks || '-'}</TableCell>
                          <TableCell>{price.created_by_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
